/**
 * /operations/attendance/bulk-adjustments — Session 3B PR 4.
 *
 * Owning agent: Tashghil. 4-card action grid + run history below
 * (mirrors LeaveBulkActionsPage shape from Session 3a §4.8).
 */

import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  ChevronRight,
  CloudOff,
  Sparkles,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { BulkAdjustmentRunItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

interface ActionCard {
  key: BulkAdjustmentRunItem["action_type"];
  title: string;
  body: string;
  icon: ReactNode;
}

function _statusToneClass(s: BulkAdjustmentRunItem["status"]): string {
  if (s === "applied") return "bg-emerald-100 text-emerald-800";
  if (s === "reverted") return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
}

export function BulkAttendanceAdjustmentsPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();

  const list = useQuery({
    queryKey: ["session3b-bulk-adjustments", legalEntityId],
    queryFn: () => session3bApi.listBulkAdjustmentRuns(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: BulkAdjustmentRunItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const totalEmployees = items.reduce((a, r) => a + r.employees_count, 0);
    return {
      total: items.length,
      attention: items.filter((r) => r.status === "draft").length,
      critical: 0,
      total_employees: totalEmployees,
    };
  }, [items]);

  const cards: ActionCard[] = [
    {
      key: "holiday_backfill",
      title: t("attendance.bulk_adjustments.card.holiday.title", "Holiday Backfill"),
      body: t(
        "attendance.bulk_adjustments.card.holiday.body",
        "Mark a date as a public holiday for every employee in scope. Tashghil computes the attendance impact + skips employees on approved leave.",
      ),
      icon: <Calendar className="h-5 w-5" />,
    },
    {
      key: "system_outage_recovery",
      title: t("attendance.bulk_adjustments.card.outage.title", "System Outage Recovery"),
      body: t(
        "attendance.bulk_adjustments.card.outage.body",
        "Roll back biometric-failure flags during a known system outage window. Restores employees to neutral attendance.",
      ),
      icon: <CloudOff className="h-5 w-5" />,
    },
    {
      key: "multi_employee_adjustment",
      title: t("attendance.bulk_adjustments.card.multi.title", "Multi-Employee Adjustment"),
      body: t(
        "attendance.bulk_adjustments.card.multi.body",
        "Apply the same manual override across a selected group — typical for shift changes or training days.",
      ),
      icon: <Users className="h-5 w-5" />,
    },
    {
      key: "roll_forward_late",
      title: t("attendance.bulk_adjustments.card.roll_forward.title", "Roll Forward Late Attendance"),
      body: t(
        "attendance.bulk_adjustments.card.roll_forward.body",
        "Forgive late_minutes for a date when traffic / infrastructure context warrants it. Audit-trailed per employee.",
      ),
      icon: <ChevronRight className="h-5 w-5" />,
    },
  ];

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("attendance.bulk_adjustments.title", "Bulk Attendance Adjustments")}
          subtitle={t(
            "attendance.bulk_adjustments.subtitle",
            "Mass corrections: holiday backfill · system-outage recovery · multi-employee adjustments. Each action records an audit row.",
          )}
        />

        <InlineAISuggestion
          agent="tashghil"
          icon={<Sparkles className="h-4 w-4" />}
          severity="info"
          headline={t(
            "attendance.bulk_adjustments.suggestion.headline",
            "Bulk operations are tracked and reversible.",
          )}
          body={t(
            "attendance.bulk_adjustments.suggestion.body",
            "Tashghil records every adjustment as a workflow_event with the actor + scope + reason. If something goes wrong, the Revert action recomputes attendance back to the original state.",
          )}
          primaryAction={{
            label: t("attendance.bulk_adjustments.suggestion.history_link", "Jump to history"),
            onClick: () => {
              const target = document.getElementById("bulk-history");
              if (target) target.scrollIntoView({ behavior: "smooth" });
            },
          }}
          suggestionKey="bulk-banner"
        />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {cards.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() =>
                showAlertTrace(
                  t(
                    "attendance.bulk_adjustments.card.click_note",
                    `${c.title} wizard — scope picker → adjustment config → preview → AI-draft reason → confirm. v1 ships the surface; the wizard step opens in the next PR.`,
                  ),
                )
              }
              className="group flex items-start gap-3 rounded-2xl border border-border-subtle bg-white p-4 text-start hover:border-brand-blue hover:shadow-sm"
            >
              <span className="rounded-lg bg-brand-blue/10 p-2 text-[#2260C9] group-hover:bg-brand-blue/20">
                {c.icon}
              </span>
              <span className="flex-1">
                <span className="block font-medium text-text-primary">{c.title}</span>
                <span className="mt-1 block text-xs text-text-secondary">{c.body}</span>
              </span>
              <ChevronRight className="h-4 w-4 self-center text-text-tertiary group-hover:text-[#2260C9]" />
            </button>
          ))}
        </div>

        <h3 id="bulk-history" className="pt-2 text-sm font-semibold text-text-primary">
          {t("attendance.bulk_adjustments.history.title", "Recent bulk runs")}
        </h3>

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length === 0 && !list.isLoading && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "attendance.bulk_adjustments.history.empty",
              "No bulk runs yet. Run one from the action cards above — Tashghil will narrate the impact before you commit.",
            )}
          </div>
        )}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("attendance.bulk_adjustments.col.action", "Action")}</th>
                  <th className="px-3 py-2">{t("attendance.bulk_adjustments.col.by", "Initiated by")}</th>
                  <th className="px-3 py-2">{t("attendance.bulk_adjustments.col.when", "When")}</th>
                  <th className="px-3 py-2">{t("attendance.bulk_adjustments.col.employees", "Employees")}</th>
                  <th className="px-3 py-2">{t("attendance.bulk_adjustments.col.status", "Status")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.run_id} className="border-t border-border-subtle">
                    <td className="px-3 py-2 text-xs text-text-secondary">{row.action_type}</td>
                    <td className="px-3 py-2 text-text-secondary">{row.initiated_by_name}</td>
                    <td className="px-3 py-2 text-text-secondary">
                      <span dir="ltr">{row.initiated_at}</span>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "attendance.bulk_adjustments.trace.employees",
                              `employees_count = number of attendance_records updated in this run. Stored in the workflow_event payload_diff JSONB so re-querying never re-counts.`,
                            ),
                          )
                        }
                      >
                        <span className="font-mono">{row.employees_count}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_statusToneClass(row.status)}`}
                      >
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: 0,
            top_kind: t("attendance.bulk_adjustments.title", "Bulk Adjustments"),
            window_label: `${counts.total_employees} employees touched`,
          }}
        />
      </aside>
    </div>
  );
}
